/**
 * Import a template file (.json): read it here, show what it contains and any
 * problems, link its list fields to this server's lists, then create it as a
 * draft (to review in the editor) or create and publish it in one go.
 */
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Template } from '@saledeed/shared';
import { normaliseScopes, notReadyIssues, validateTemplate } from '@saledeed/doc-engine';
import { api } from '../api/client';
import type { ListSummary, TemplateSummary } from '../api/types';
import { Badge, Button, ErrorBox, Field, Modal, Notice, TextInput } from '../components/ui';
import { listsFor, MAX_TEMPLATE_FILE_BYTES, missingLists, readTemplateFile, relinkLists, type ReadResult } from './template-file';

interface Done { template: TemplateSummary; published: boolean; publishError?: unknown }

export function TemplateFileDialog({ open, onClose, onOpen }: { open: boolean; onClose: () => void; onOpen: (id: string) => void }) {
  const qc = useQueryClient();
  const lists = useQuery({ queryKey: ['lists'], queryFn: () => api<{ lists: ListSummary[] }>('GET', '/api/lists'), enabled: open });
  const [fileName, setFileName] = useState('');
  const [read, setRead] = useState<ReadResult | null>(null);
  const [title, setTitle] = useState('');
  const [links, setLinks] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState<Done | null>(null);

  const reset = () => { setFileName(''); setRead(null); setTitle(''); setLinks({}); setError(null); setDone(null); };
  const close = () => { if (!busy) { reset(); onClose(); } };

  const all = lists.data?.lists ?? [];
  const source = read?.ok ? read.template : null;
  const needs = useMemo(() => (source ? missingLists(source, all) : []), [source, all]);
  // Each missing list goes to the best uploaded match unless the admin chose otherwise.
  const linked = useMemo(
    () => Object.fromEntries(needs.map(n => [n.id, links[n.id] ?? listsFor(n, all)[0]?.id ?? ''])),
    [needs, links, all],
  );
  // Fields inside a block are asked for each seller/buyer/property, as the server stores them.
  const final: Template | null = useMemo(
    () => (source ? normaliseScopes({ ...relinkLists(source, linked), title: title.trim() || source.title }).template : null),
    [source, linked, title],
  );
  const issues = useMemo(() => (final ? validateTemplate(final) : []), [final]);
  // Publishing also needs what the Worker checks before publishing (the executive's form must match the deed).
  const notReady = notReadyIssues(issues);
  const errors = issues.filter(i => i.severity === 'error');
  const warnings = issues.filter(i => i.severity === 'warning');

  const choose = async (file: File | undefined) => {
    reset();
    if (!file) return;
    setFileName(file.name);
    if (file.size > MAX_TEMPLATE_FILE_BYTES) {
      setRead({ ok: false, message: 'This file is larger than 4 MB, so it is not a template file.', details: [] });
      return;
    }
    const result = readTemplateFile(await file.text());
    setRead(result);
    if (!result.ok) return;
    setTitle(result.template.title);
  };

  const create = async (publish: boolean) => {
    if (!final) return;
    setBusy(true);
    setError(null);
    let created: TemplateSummary | null = null;
    try {
      created = (await api<{ template: TemplateSummary }>('POST', '/api/templates', { body: { template: final } })).template;
      if (publish) await api('POST', `/api/templates/${created.id}/publish`, { body: { revision: created.draftRevision } });
      setDone({ template: created, published: publish });
    } catch (e) {
      // Created but not published: show the draft (pressing again would make a second copy).
      if (created) setDone({ template: created, published: false, publishError: e });
      else setError(e);
    } finally {
      setBusy(false);
      await qc.invalidateQueries({ queryKey: ['templates'] });
    }
  };

  return (
    <Modal open={open} wide title="Import a template file" onClose={close}>
      {done ? (
        <div className="flex flex-col gap-3">
          <Notice tone="success">
            {done.published
              ? <>Published <b>{final?.title}</b> as v1 (code <code>{done.template.templateCode}</code>). Next: open a project, add this template to it, and start a document.</>
              : <>Created <b>{final?.title}</b> as a draft (code <code>{done.template.templateCode}</code>). Open it to check it, then press Publish.</>}
          </Notice>
          {done.publishError !== undefined && <><p className="text-sm">It was not published:</p><ErrorBox error={done.publishError} /></>}
          <div className="flex justify-end gap-2">
            <Button onClick={close}>Close</Button>
            <Button tone="primary" onClick={() => { const id = done.template.id; reset(); onOpen(id); }}>Open in the editor</Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-slate-600">
            Choose a <strong>.json</strong> template file (for example one saved with <em>Save as file</em> in the editor,
            or the sample <code>docs/testing/sample-full-sale-deed.template.json</code>). Nothing is saved until you press a button below.
          </p>
          <label className="flex flex-col text-sm">Template file
            <input type="file" accept=".json,application/json" disabled={busy} className="text-sm"
              onChange={e => { void choose(e.target.files?.[0]); e.target.value = ''; }} />
          </label>

          {read && !read.ok && (
            <div role="alert" className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900">
              <p><strong>{fileName}</strong>: {read.message}</p>
              {read.details.length > 0 && <ul className="mt-1 list-disc pl-5 text-xs">{read.details.map((d, i) => <li key={i}>{d}</li>)}</ul>}
            </div>
          )}

          {source && final && (
            <>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <strong>{fileName}</strong>
                <Badge tone="blue">{source.natureOfDocument}</Badge>
                <Badge>{source.fields.length} fields</Badge>
                <Badge>{source.blocks.length} blocks</Badge>
                {errors.length > 0 ? <Badge tone="red">{errors.length} problem{errors.length === 1 ? '' : 's'}</Badge>
                  : notReady.length > 0 ? <Badge tone="amber">Not ready to publish</Badge>
                  : <Badge tone="green">Ready to publish</Badge>}
                {warnings.length > 0 && <Badge tone="amber">{warnings.length} note{warnings.length === 1 ? '' : 's'}</Badge>}
              </div>
              <Field label="Name of the template">
                {id => <TextInput id={id} value={title} onChange={e => setTitle(e.target.value)} maxLength={200} disabled={busy} />}
              </Field>

              {needs.map(need => {
                const options = listsFor(need, all);
                return (
                  <div key={need.id} className="rounded border border-slate-200 p-3 text-sm">
                    <p>
                      <b>{need.fields.map(f => f.label).join(', ')}</b> take their choices from a list
                      with the columns {need.columns.map(c => `"${c}"`).join(', ')}.
                    </p>
                    {options.length ? (
                      <label className="mt-2 flex flex-col">Use this uploaded list
                        <select className="rounded border border-slate-300 px-2 py-1" value={linked[need.id] ?? ''} disabled={busy}
                          onChange={e => setLinks({ ...links, [need.id]: e.target.value })}>
                          {options.map(l => <option key={l.id} value={l.id}>{l.name} ({l.rowCount} rows)</option>)}
                          <option value="">None: the executive types these by hand</option>
                        </select>
                      </label>
                    ) : (
                      <Notice tone="warning">
                        No uploaded list has these columns{lists.isPending ? ' (still loading lists)' : ''}. Upload one on the <b>Lists</b> page
                        (the sample is <code>docs/testing/sample-locations.csv</code>) and choose this file again, or import now and the
                        executive types these by hand.
                      </Notice>
                    )}
                  </div>
                );
              })}

              {errors.length > 0 && (
                <div role="alert" className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-900">
                  <p>This template cannot be published until these are fixed. Create it as a draft and fix them in the editor.</p>
                  <ul className="mt-1 list-disc pl-5 text-xs">{errors.slice(0, 8).map((x, i) => <li key={i}>{x.message}</li>)}</ul>
                </div>
              )}
              {errors.length === 0 && notReady.length > 0 && (
                <Notice tone="warning">
                  Executives would get the wrong form from this template, so it cannot be published yet: {notReady.map(i => i.message).join(' ')}
                  {' '}Create it as a draft; the editor shows a button to fix it.
                </Notice>
              )}
              {warnings.length > 0 && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-slate-700">Notes ({warnings.length})</summary>
                  <ul className="mt-1 list-disc pl-5 text-xs text-slate-700">{warnings.slice(0, 20).map((x, i) => <li key={i}>{x.message}</li>)}</ul>
                </details>
              )}

              <ErrorBox error={error} />
              <div className="flex justify-end gap-2">
                <Button disabled={busy} onClick={() => void create(false)}>Create draft</Button>
                <Button tone="primary" disabled={busy || errors.length > 0 || notReady.length > 0} onClick={() => void create(true)}>
                  {busy ? 'Working…' : 'Create and publish'}
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
